import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import announce from '@Utils/announce';
import { flattenNodeTree } from './Shared/queryTreeUtils';
import { allowFocusRing } from './Shared/focusManagement';

const defaultTargetMode = {
  active: false,
  source: null,
  targetIds: [],
  activateTargetMode: () => {},
  canActivateTargetMode: () => false,
  cancelTargetMode: () => {},
  confirmTarget: () => {},
  handleTargetKeyDown: () => {},
  registerSource: () => {},
  registerTarget: () => {},
};

const TargetModeContext = createContext(defaultTargetMode);

function getNodeLabel(node) {
  return node?.groupName || node?.displayName || 'item';
}

function collectDescendantIds(node, ids = new Set()) {
  (node?.items || []).forEach((item) => {
    if (!item?.uuid) return;
    ids.add(item.uuid);
    collectDescendantIds(item, ids);
  });

  return ids;
}

function TargetModeProvider({ children, nodesList, addToBuilderAction, mergeNodeItemAction }) {
  const [targetMode, setTargetMode] = useState(null);
  const targetRefs = useRef(new Map());
  const sourceRefs = useRef(new Map());
  const pendingFocusRef = useRef(null);

  const targetIds = useMemo(() => targetMode?.targetIds || [], [targetMode]);

  useEffect(() => {
    const workspace = document.querySelector('.query-builder');
    workspace?.classList.toggle('query-builder--target-mode', Boolean(targetMode));

    return () => workspace?.classList.remove('query-builder--target-mode');
  }, [targetMode]);

  const focusTarget = useCallback(
    (uuid) => {
      const target = targetRefs.current.get(uuid);
      // Target Mode is only ever entered by keyboard/AT, but a screen reader activates the drag
      // button with a click, which re-arms Leyden's `s-els-no-focus` outline suppression. Clear it
      // or the target we focus here has no visible ring until the user presses Tab.
      allowFocusRing();
      target?.focus();
    },
    [targetRefs]
  );

  // Screen readers correlate a focus change to the user's gesture by how soon after it the
  // focus happens. requestAnimationFrame waits for the next paint on top of React's own
  // post-commit effect timing, which is far enough removed that NVDA/JAWS stop tracking the
  // change: real DOM focus (and the CSS focus ring) lands correctly, but the AT's virtual
  // cursor/highlight does not follow. useLayoutEffect fires synchronously right after the
  // target buttons are committed to the DOM, with no extra frame of delay, which keeps focus
  // movement close enough to the gesture for AT to follow it.
  useLayoutEffect(() => {
    if (!targetMode || targetIds.length === 0) return;

    focusTarget(targetIds[0]);
    announce(
      `Target mode active for ${getNodeLabel(targetMode.source.node)}. Tab to move between grouping targets, Enter to group, or Escape to cancel.`
    );
  }, [focusTarget, targetIds, targetMode]);

  useLayoutEffect(() => {
    if (targetMode !== null || !pendingFocusRef.current) return;
    const { sourceId } = pendingFocusRef.current;
    pendingFocusRef.current = null;
    allowFocusRing();
    sourceRefs.current.get(sourceId)?.focus();
  }, [targetMode]);

  const registerTarget = useCallback((uuid, element) => {
    if (element) {
      targetRefs.current.set(uuid, element);
    } else {
      targetRefs.current.delete(uuid);
    }
  }, []);

  const registerSource = useCallback((sourceId, element) => {
    if (element) {
      sourceRefs.current.set(sourceId, element);
    } else {
      sourceRefs.current.delete(sourceId);
    }
  }, []);

  const cancelTargetMode = useCallback(() => {
    if (!targetMode) return;

    const { source } = targetMode;
    pendingFocusRef.current = { sourceId: source.sourceId };
    setTargetMode(null);
    announce(`Target mode cancelled for ${getNodeLabel(source.node)}.`);
  }, [targetMode]);

  // Document-level Escape so cancelling works even if focus has drifted off a target button
  // (e.g. a target unmounted, or the browser moved focus to body when the tree went inert).
  useEffect(() => {
    if (!targetMode) return undefined;

    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        cancelTargetMode();
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [cancelTargetMode, targetMode]);

  /**
   * Returns true when Target Mode actually started, so callers can fall back to a
   * plain add when there is nothing in the tree to group against.
   */
  const activateTargetMode = useCallback(
    (source) => {
      if (targetMode) return false;

      const allNodes = flattenNodeTree(nodesList);
      const excludedIds = source.kind === 'tree' ? collectDescendantIds(source.node, new Set([source.node.uuid])) : new Set();
      const availableTargetIds = allNodes.filter((node) => node?.uuid && !excludedIds.has(node.uuid)).map((node) => node.uuid);

      if (availableTargetIds.length === 0) {
        announce(`No grouping target is available for ${getNodeLabel(source.node)}.`);
        return false;
      }

      setTargetMode({ source, targetIds: availableTargetIds });
      return true;
    },
    [nodesList, targetMode]
  );

  const flattenedNodes = useMemo(() => flattenNodeTree(nodesList), [nodesList]);

  const canActivateTargetMode = useCallback(
    (source) => {
      if (!source?.kind) return false;

      const excludedIds =
        source.kind === 'tree' ? collectDescendantIds(source.node, new Set([source.node.uuid])) : new Set();
      return flattenedNodes.some((node) => node?.uuid && !excludedIds.has(node.uuid));
    },
    [flattenedNodes]
  );

  const confirmTarget = useCallback(
    (targetNode) => {
      if (!targetMode || !targetNode?.uuid) return;

      const { source } = targetMode;
      const sourceName = getNodeLabel(source.node);
      const targetName = getNodeLabel(targetNode);

      if (source.kind === 'tree') {
        mergeNodeItemAction({ key: source.node.uuid, obj: source.node }, nodesList, { key: targetNode.uuid });
      } else {
        const isForm = source.node?.type === 'form';
        const dragId = isForm ? source.node.displayName : source.node.id;
        addToBuilderAction(source.node, 'default', { dragId, dropId: targetNode.uuid, isForm });
      }

      pendingFocusRef.current = { sourceId: `tree:${targetNode.uuid}` };
      setTargetMode(null);
      announce(`${sourceName} grouped with ${targetName}.`);
    },
    [addToBuilderAction, mergeNodeItemAction, nodesList, targetMode]
  );

  // Escape is handled by the document-level listener above, not here: this event bubbles to it, so
  // a local branch would only cancel twice.
  const handleTargetKeyDown = useCallback(
    (event, uuid) => {
      if (!targetMode || event.key !== 'Tab') return;

      event.preventDefault();
      const currentIndex = targetIds.indexOf(uuid);
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = (currentIndex + direction + targetIds.length) % targetIds.length;
      focusTarget(targetIds[nextIndex]);
    },
    [focusTarget, targetIds, targetMode]
  );

  const value = useMemo(
    () => ({
      active: Boolean(targetMode),
      source: targetMode?.source || null,
      targetIds,
      activateTargetMode,
      canActivateTargetMode,
      cancelTargetMode,
      confirmTarget,
      handleTargetKeyDown,
      registerSource,
      registerTarget,
    }),
    [
      activateTargetMode,
      canActivateTargetMode,
      cancelTargetMode,
      confirmTarget,
      handleTargetKeyDown,
      registerSource,
      registerTarget,
      targetIds,
      targetMode,
    ]
  );

  return <TargetModeContext.Provider value={value}>{children}</TargetModeContext.Provider>;
}

TargetModeProvider.propTypes = {
  children: PropTypes.node,
  nodesList: PropTypes.array,
  addToBuilderAction: PropTypes.func.isRequired,
  mergeNodeItemAction: PropTypes.func.isRequired,
};

function useTargetMode() {
  return useContext(TargetModeContext);
}

export { TargetModeProvider, useTargetMode };
